import React, { FC, useEffect, useState } from 'react'

import { Skeleton } from '@mui/material'
import clsx from 'clsx'
import Select from 'react-select'
import { where } from 'firebase/firestore'

import InfiniteScrollDataTable from '../../../../../../components/common/tables/InfiniteScrollDataTable'
import ViewsLoader from '../../../../../../components/loader/ViewsLoader'
import { ScoreCardOption } from '../../../components/ScoreCardOption'
import { JudgingBatch } from '../JudgingBatch/JudgingBatch'
import { IconCheck } from '../../../../../../components/icons/IconCheck'

import FirestoreService from '../../../../../../services/firestoreService'
import { cn } from '../../../../../../lib/utils'

import { useAppSelector } from '../../../../../../store/hooks'
import { selectProfileData } from '../../../../../../store/user/userSlice'

import { ScoreCardModel } from '../../../../../../models/score-cards/score-cards.model'

import {
  IEventFees,
  IJudgeOption,
  IScoreCardDiscipline,
} from '../../../../../../models/event-fees/event-fees.interface'
import {
  IUpdateFeesKeys,
  IUpdateRegistrationByDayName,
  RegistrationFeesTypeEx,
} from '../../ManageClinicNOtherOperationsTab'
import { IScoreCardInterface } from '../../../../../../models/score-cards/score-cards.interface'

import {
  disciplineOptions,
  getJudgeValues,
  getScoreCardsValues,
} from '../../data/judging-tab-utils'

import { CONST } from '../../../../../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

const initialScoreCardHunterJumperOptions: IJudgeOption[] = [
  { label: 'Equitation', value: 'Equitation', disable: false },
  { label: 'Hunters', value: 'Hunters', disable: false },
]

const initialFastestTimeScoreCard: IJudgeOption[] = [
  {
    label: 'Add fastest time score card',
    value: 'fastest-time-score-card',
    disable: false,
    isBorderTop: true,
    onAddScoreCard: true,
    scoreCardType: 'fastest',
  },
]

const initialOptimumTimeScoreCard: IJudgeOption[] = [
  {
    label: 'Add optimum time score card',
    value: 'optimum-time-score-card',
    disable: false,
    isBorderTop: true,
    onAddScoreCard: true,
    scoreCardType: 'optimum',
  },
]

const initialScoreCard: IJudgeOption[] = [
  {
    label: 'No score cards',
    value: '',
    disable: true,
  },
]

export interface ISelectedIds {
  uuid: string
  parentId?: string
}

interface JudgingTableProps {
  onChangeJudge: (
    row: RegistrationFeesTypeEx,
    values: { label: string; value: string }[]
  ) => Promise<void>
  updateRegistrationByDayName: (state: IUpdateRegistrationByDayName) => Promise<void>
  updateFeesKeys: IUpdateFeesKeys
  registrationFees: IEventFees | null
  judgesOptions: IJudgeOption[] | null
}

export const JudgingTable: FC<JudgingTableProps> = ({
  onChangeJudge,
  updateRegistrationByDayName,
  updateFeesKeys,
  registrationFees,
  judgesOptions,
}) => {
  const profileData = useAppSelector(selectProfileData)

  const [currentLoading, currentSetLoading] = useState(false)
  const [scoreCarLoading, setScoreCarLoading] = useState(false)
  const [scoreCardsByUser, setScoreCardsByUser] = useState<IScoreCardInterface[] | null>(null)
  const [scoreCardHunterJumperOptions, setScoreCardHunterJumperOptions] = useState<IJudgeOption[]>([
    ...initialScoreCardHunterJumperOptions,
    ...initialFastestTimeScoreCard,
    ...initialOptimumTimeScoreCard,
  ])
  const [isBatchJudging, setIsBatchJudging] = useState(false)
  const [selectedIds, setSelectedIds] = useState<ISelectedIds[]>([])

  const getScoreCardsByUser = async () => {
    const scoreCards: IScoreCardInterface[] = []

    setScoreCarLoading(true)
    try {
      const scoreCardsSnaps = await FirestoreService.filterItems(COLLECTIONS.SCORE_CARDS.NAME, [
        where(COLLECTIONS.SCORE_CARDS.FIELDS.OWNER.KEY, '==', profileData.id),
      ])

      scoreCardsSnaps.forEach((doc) => {
        const item = ScoreCardModel.fromFirestoreDoc(doc).toObject()
        scoreCards.push(item)
      })
      setScoreCardsByUser(scoreCards)
    } catch (error) {
      console.error(error)
    } finally {
      setScoreCarLoading(false)
    }
  }

  useEffect(() => {
    getScoreCardsByUser().then()
  }, [profileData.id])

  useEffect(() => {
    const fastestTimeScoreCard = scoreCardsByUser?.filter(
      (scoreCard) => scoreCard.type === 'hunter-jumper' && scoreCard.scoreCardType !== 'optimum'
    )
    const optimumScoreCard = scoreCardsByUser?.filter(
      (scoreCard) => scoreCard.type === 'hunter-jumper' && scoreCard.scoreCardType === 'optimum'
    )

    setScoreCardHunterJumperOptions([
      ...initialScoreCardHunterJumperOptions,
      ...initialFastestTimeScoreCard,
      ...(fastestTimeScoreCard?.[0]
        ? fastestTimeScoreCard.map((scoreCard) => ({
            label: scoreCard.name,
            value: scoreCard.id ?? '',
            disable: false,
            isCustom: true,
            scoreCardType: scoreCard.scoreCardType,
          }))
        : initialScoreCard),
      ...initialOptimumTimeScoreCard,
      ...(optimumScoreCard?.[0]
        ? optimumScoreCard.map((scoreCard) => ({
            label: scoreCard.name,
            value: scoreCard.id ?? '',
            disable: false,
            isCustom: true,
            scoreCardType: scoreCard.scoreCardType,
          }))
        : initialScoreCard),
    ])
  }, [scoreCardsByUser])

  const onChangeSelect = async ({
    row,
    discipline,
    scoreCard,
    updated,
  }: {
    row: RegistrationFeesTypeEx
    discipline?: IScoreCardDiscipline
    scoreCard?: string
    updated: { [_: string]: string | null }
  }) => {
    await updateFeesKeys(updated, [{ uuid: row.uuid, parentId: row.parentId }])
    await updateRegistrationByDayName({
      feeUuid: [row.uuid],
      sheetId: scoreCard ? scoreCard : row.scoreCard || '',
      discipline: discipline ? discipline : row.discipline || null,
    })
  }

  const isFilled = (row: RegistrationFeesTypeEx) => ({
    borderColor: '#D3DAEE',
    backgroundColor:
      row.judges && row.judges.length > 0 && row.scoreCard && row.discipline ? '#F6F7FB' : '#fff',
  })

  const columnsJudging = [
    {
      name: (
        <span className="text-gray-400 font-normal text-sm">
          <span className="whitespace-nowrap">Class</span>
        </span>
      ),
      cell: (row: RegistrationFeesTypeEx) =>
        row.loading ? (
          <div className="p-2 w-full">
            <Skeleton
              variant="rounded"
              width="98%"
              height={56}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          </div>
        ) : (
          <div
            className={clsx(
              'my-2 p-2 min-h-[62px] w-full rounded-md flex items-center border border-SeabiscuitLightThemeColorD3',
              row.judges && row.judges.length > 0 && row.scoreCard && 'bg-SeabiscuitGrayThemeColor'
            )}
          >
            <div>
              {row.name}
              {row.order ? (
                <span className="m-1 whitespace-nowrap text-[14px] text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rounded-full py-0.5 px-3">
                  {row.order}
                </span>
              ) : null}
            </div>
          </div>
        ),
      width: '28%',
    },
    {
      name: (
        <span className="text-gray-400 font-normal text-[0.875rem]">
          <span className="whitespace-nowrap">Discipline</span>
        </span>
      ),
      cell: (row: RegistrationFeesTypeEx) =>
        row.loading ? (
          <div className="p-2 w-full">
            <Skeleton
              variant="rounded"
              width="98%"
              height={56}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          </div>
        ) : (
          <div className="flex items-center w-full pl-2">
            <Select
              className="w-full"
              classNamePrefix="clinic-select"
              isClearable={false}
              isSearchable={true}
              placeholder="Select Discipline"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  ...isFilled(row),
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  color: state.isDisabled ? '#122B4680' : baseStyles.color,
                }),
              }}
              value={getJudgeValues(row.discipline ?? '', disciplineOptions)}
              options={disciplineOptions}
              isOptionDisabled={(option: IJudgeOption) => option.disable}
              onChange={async (value) => {
                currentSetLoading(true)
                if (value)
                  await onChangeSelect({
                    row: row,
                    discipline: value.value as IScoreCardDiscipline,
                    updated: {
                      discipline: value.value,
                      scoreCard: null,
                      judges: null,
                    },
                  })
                currentSetLoading(false)
              }}
            />
          </div>
        ),
      width: '16%',
    },
    {
      name: (
        <span className="text-gray-400 font-normal text-[0.875rem]">
          <span className="whitespace-nowrap">Score cards</span>
        </span>
      ),
      cell: (row: RegistrationFeesTypeEx) =>
        row.loading ? (
          <div className="p-2 w-full">
            <Skeleton
              variant="rounded"
              width="98%"
              height={56}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          </div>
        ) : (
          <div className="flex items-center w-full pl-2">
            <Select
              className="w-full"
              classNamePrefix="clinic-select"
              isClearable={false}
              isSearchable={true}
              placeholder="Select score card"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              isLoading={scoreCarLoading}
              onMenuOpen={getScoreCardsByUser}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  ...isFilled(row),
                }),
              }}
              value={getJudgeValues(
                row.scoreCard ?? '',
                getScoreCardsValues({ discipline: row.discipline, scoreCardHunterJumperOptions })
              )}
              options={getScoreCardsValues({
                discipline: row.discipline,
                scoreCardHunterJumperOptions,
              })}
              components={{ Option: (props) => <ScoreCardOption {...props} row={row} /> }}
              onChange={async (value) => {
                currentSetLoading(true)
                if (value)
                  await onChangeSelect({
                    row,
                    scoreCard: value.value,
                    updated: {
                      scoreCard: value.value,
                      judges: null,
                    },
                  })
                currentSetLoading(false)
              }}
            />
          </div>
        ),
      width: '28%',
    },
    {
      name: (
        <span className="text-gray-400 font-normal text-[0.875rem]">
          <span className="whitespace-nowrap">Judges</span>
        </span>
      ),
      cell: (row: RegistrationFeesTypeEx) =>
        row.loading ? (
          <div className="p-2 w-full">
            <Skeleton
              variant="rounded"
              width="100%"
              height={56}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          </div>
        ) : (
          <div className="flex items-center w-full pl-2">
            <Select
              className="w-full"
              classNamePrefix="clinic-select"
              isClearable={false}
              isSearchable={false}
              isMulti={true}
              placeholder="Select judges"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  ...isFilled(row),
                }),
              }}
              value={
                row.judges && row.judges.length > 0 && judgesOptions && judgesOptions.length > 0
                  ? row.judges.map((judge) => getJudgeValues(judge ?? '', judgesOptions))
                  : []
              }
              options={judgesOptions ?? []}
              onChange={async (values) => {
                currentSetLoading(true)
                if (values) await onChangeJudge(row, values as IJudgeOption[])
                currentSetLoading(false)
              }}
            />
          </div>
        ),
      width: 'calc(28% - 30px)',
    },
    {
      cell: (row: RegistrationFeesTypeEx) => {
        const isSelected = selectedIds.find((item) => item.uuid === row.uuid)?.uuid
        return (
          <div className="w-full flex items-center justify-end">
            {isBatchJudging && (
              <button
                type="button"
                className={cn(
                  'border border-solid w-[20px] h-[20px] text-[#00B6AA] rounded-full flex items-center justify-center',
                  isSelected ? 'border-[#00B6AA]' : 'border-[#D3DAEE]'
                )}
                onClick={() => onSelected(row.uuid, row.parentId)}
              >
                {isSelected && <IconCheck />}
              </button>
            )}
          </div>
        )
      },
      width: '30px',
    },
  ]

  const newRegistrationFees = registrationFees?.registrationFees?.flatMap((fee) => {
    const feeData = [
      {
        ...fee,
        value: fee.uuid || '',
        label: `${fee.name || ''}`,
        hasChildren: fee.children && Array.isArray(fee.children) && fee.children.length > 0,
      },
    ]

    if (fee.children && Array.isArray(fee.children) && fee.children.length > 0) {
      const childrenData = fee.children.map((child) => ({
        ...child,
        value: child.uuid || '',
        label: `${child.name || ''}`,
        parentId: fee.uuid || '',
      }))

      return childrenData as RegistrationFeesTypeEx[]
    }

    return feeData
  })

  const onSelected = (uuid: string, parentId?: string) => {
    const isSelected = selectedIds.find((item) => item.uuid === uuid)?.uuid

    if (isSelected) {
      const updated = selectedIds.filter((item) => item.uuid !== uuid)
      setSelectedIds([...updated])
    } else {
      setSelectedIds([...selectedIds, { uuid, parentId }])
    }
  }

  return (
    <div className="relative z-10 mt-4">
      {currentLoading && (
        <div className="w-full bg-white/30 h-full fixed z-30 left-0 top-0 flex justify-center items-center">
          <ViewsLoader size="lg" color="#F70763" />
        </div>
      )}
      <JudgingBatch
        getScoreCardsByUser={getScoreCardsByUser}
        scoreCardHunterJumperOptions={scoreCardHunterJumperOptions}
        judgesOptions={judgesOptions}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        isBatchJudging={isBatchJudging}
        setIsBatchJudging={setIsBatchJudging}
        updateFeesKeys={updateFeesKeys}
        updateRegistrationByDayName={updateRegistrationByDayName}
        newRegistrationFees={newRegistrationFees}
      />
      <InfiniteScrollDataTable
        hasMore={false}
        className="exhibitorListTable"
        columns={columnsJudging}
        data={[...(newRegistrationFees ?? [])]}
      />
    </div>
  )
}
